import { ReactNode, HTMLProps } from 'react';

// Components
import { Badge } from '@mui/material';

// Models
import { ColorSchema } from 'vendor/material-minimal/palette';

// Utils
import { getDOMProps, isRenderable } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';
import {
  U21PulseIndicator,
  U21PulseIndicatorProps,
} from 'app/shared/u21-ui/components/display/U21PulseIndicator';

declare module '@mui/material' {
  interface BadgePropsColorOverrides {
    special: true;
  }
}

export interface U21BadgeProps extends Omit<
  HTMLProps<HTMLSpanElement>,
  'content'
> {
  children?: ReactNode;
  color?: 'default' | 'unset' | 'white' | ColorSchema;
  content?: ReactNode;
  hidden?: boolean;
  pulseProps?: U21PulseIndicatorProps;
  variant?: 'dot' | 'pulse' | 'standard';
}

export const U21Badge = ({
  color = 'primary',
  content,
  hidden,
  children = null,
  pulseProps,
  variant = 'standard',
  ...rest
}: U21BadgeProps) => {
  if (hidden) {
    return <>{children}</>;
  }

  if (variant === 'pulse') {
    return (
      <PulseBadge
        $color="unset"
        badgeContent={<U21PulseIndicator {...pulseProps} />}
        variant="standard"
        {...getDOMProps(rest)}
      >
        {children}
      </PulseBadge>
    );
  }

  if (!content && content !== 0 && variant !== 'dot') {
    return <>{children}</>;
  }

  return (
    <StyledBadge
      $color={color}
      badgeContent={content}
      color={color === 'unset' || color === 'white' ? 'default' : color}
      variant={variant}
      {...getDOMProps(rest)}
    >
      {children}
    </StyledBadge>
  );
};

const StyledBadge = styled(Badge)<{
  $color: Exclude<U21BadgeProps['color'], undefined>;
}>`
  .MuiBadge-badge {
    ${(props) => {
      if (props.$color === 'default') {
        return css`
          background-color: ${props.theme.palette.background.neutral};
        `;
      }
      if (props.$color === 'white') {
        return css`
          background-color: ${props.theme.palette.background.paper};
        `;
      }
      return css``;
    }}

    ${(props) => {
      // if no children, then just treat it as an inline indicator
      if (!isRenderable(props.children)) {
        return css`
          position: static;
          transform: none;
        `;
      }
      return css``;
    }}
`;

const PulseBadge = styled(StyledBadge)`
  .MuiBadge-badge {
    pointer-events: none;
  }
`;
